import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import BannerImage from '@barbershop/shared/assets/banner.png'
import NotifySound from '@barbershop/shared/assets/notify.mp3'
import { Box, Image, Text, VStack } from '@chakra-ui/react'
import useSound from 'use-sound'

import { getVisibleBoardStations, tv, TV_BOARD_FRAME } from '../model/board-layout'
import {
    createBoardQueueEventSource,
    isBoardBranchNotFound,
    parseBoardQueueStreamSnapshot,
} from '../model/board-queue-stream'
import { hasBoardStatusChange } from '../model/board-status-notification'
import type { BoardQueueOutput, BoardStation } from '../model/board-types'
import { sortStationsForBoard } from '../model/queue-view'
import { BoardMasterCard } from './board-master-card'
import { BoardNotFoundState } from './board-not-found-state'

const BOARD_STATUS_NOTIFICATION_REPEAT_COUNT = 3
const BOARD_STATUS_NOTIFICATION_REPEAT_DELAY_MS = 2000
const BOARD_STREAM_RECONNECT_DELAY_MS = 3000

interface BoardPageProps {
    branchId: number
}

export const BoardPage = ({ branchId }: BoardPageProps) => {
    const [isError, setIsError] = useState(false)
    const [isBranchNotFound, setIsBranchNotFound] = useState(false)
    const [isStreaming, setIsStreaming] = useState(false)
    const [isNotificationAudioEnabled, setIsNotificationAudioEnabled] = useState(false)
    const [queue, setQueue] = useState<BoardQueueOutput>()
    const previousVisibleStationsRef = useRef<BoardStation[]>([])
    const notificationRepeatTimeoutsRef = useRef<Array<ReturnType<typeof globalThis.setTimeout>>>(
        []
    )
    const [playNotify] = useSound(NotifySound, {
        interrupt: true,
        soundEnabled: isNotificationAudioEnabled,
    })
    const stations = useMemo(() => sortStationsForBoard(queue?.stations), [queue?.stations])
    const visibleStations = useMemo(() => getVisibleBoardStations(stations), [stations])
    const isLoading = !queue && !isError
    const hasStations = visibleStations.length > 0

    const clearPendingNotificationRepeats = useCallback(() => {
        for (const timeoutId of notificationRepeatTimeoutsRef.current) {
            globalThis.clearTimeout(timeoutId)
        }
        notificationRepeatTimeoutsRef.current = []
    }, [])

    useEffect(() => {
        let eventSource: EventSource | undefined
        let reconnectTimeoutId: ReturnType<typeof globalThis.setTimeout> | undefined
        let isDisposed = false

        const handleOpen = () => {
            setIsError(false)
            setIsStreaming(true)
        }

        const handleMessage = (event: MessageEvent<string>) => {
            try {
                const queue = parseBoardQueueStreamSnapshot(event.data)

                setQueue(queue)
                setIsError(false)
                setIsStreaming(true)
            } catch {
                setIsError(true)
                setIsStreaming(false)
            }
        }

        const handleStreamClosed = async () => {
            disconnect()

            const isNotFound = await isBoardBranchNotFound(branchId).catch(() => false)

            if (isDisposed) {
                return
            }

            if (isNotFound) {
                setIsBranchNotFound(true)

                return
            }

            reconnectTimeoutId = globalThis.setTimeout(connect, BOARD_STREAM_RECONNECT_DELAY_MS)
        }

        const handleError = () => {
            setIsError(true)
            setIsStreaming(false)

            if (eventSource?.readyState === EventSource.CLOSED) {
                void handleStreamClosed()
            }
        }

        const connect = () => {
            eventSource = createBoardQueueEventSource(branchId)
            eventSource.addEventListener('open', handleOpen)
            eventSource.addEventListener('message', handleMessage)
            eventSource.addEventListener('queue-update', handleMessage)
            eventSource.addEventListener('error', handleError)
        }

        const disconnect = () => {
            eventSource?.removeEventListener('open', handleOpen)
            eventSource?.removeEventListener('message', handleMessage)
            eventSource?.removeEventListener('queue-update', handleMessage)
            eventSource?.removeEventListener('error', handleError)
            eventSource?.close()
            eventSource = undefined
        }

        connect()

        return () => {
            isDisposed = true
            globalThis.clearTimeout(reconnectTimeoutId)
            disconnect()
        }
    }, [branchId])

    useEffect(() => {
        return clearPendingNotificationRepeats
    }, [clearPendingNotificationRepeats])

    useEffect(() => {
        const enableNotificationAudio = () => {
            setIsNotificationAudioEnabled(true)
        }

        globalThis.addEventListener('click', enableNotificationAudio, { once: true })
        globalThis.addEventListener('keydown', enableNotificationAudio, { once: true })
        globalThis.addEventListener('touchstart', enableNotificationAudio, { once: true })
        globalThis.addEventListener('touchend', enableNotificationAudio, { once: true })

        return () => {
            globalThis.removeEventListener('click', enableNotificationAudio)
            globalThis.removeEventListener('keydown', enableNotificationAudio)
            globalThis.removeEventListener('touchstart', enableNotificationAudio)
            globalThis.removeEventListener('touchend', enableNotificationAudio)
        }
    }, [])

    useEffect(() => {
        const hasStatusChange = hasBoardStatusChange(
            previousVisibleStationsRef.current,
            visibleStations
        )

        previousVisibleStationsRef.current = visibleStations

        if (!hasStatusChange) {
            return
        }

        if (!isNotificationAudioEnabled) {
            return
        }

        clearPendingNotificationRepeats()
        playNotify()

        notificationRepeatTimeoutsRef.current = Array.from(
            { length: BOARD_STATUS_NOTIFICATION_REPEAT_COUNT - 1 },
            (_, repeatIndex) =>
                globalThis.setTimeout(
                    () => playNotify(),
                    BOARD_STATUS_NOTIFICATION_REPEAT_DELAY_MS * (repeatIndex + 1)
                )
        )

        return
    }, [clearPendingNotificationRepeats, isNotificationAudioEnabled, playNotify, visibleStations])

    if (isBranchNotFound) {
        return <BoardNotFoundState variant='branch-not-found' />
    }

    return (
        <Box bg='#f4f4f5' color='#212121' h='100dvh' overflow='hidden' position='relative' w='full'>
            <Box
                h={tv(TV_BOARD_FRAME.height)}
                left='50%'
                position='absolute'
                top='50%'
                transform='translate(-50%, -50%)'
                w={tv(TV_BOARD_FRAME.width)}
            >
                <Image
                    alt='Катит'
                    h={tv(TV_BOARD_FRAME.banner.height)}
                    left={tv(TV_BOARD_FRAME.banner.x)}
                    objectFit='cover'
                    pointerEvents='none'
                    position='absolute'
                    src={BannerImage}
                    top={tv(TV_BOARD_FRAME.banner.y)}
                    w={tv(TV_BOARD_FRAME.banner.width)}
                />
                {hasStations ? (
                    <Box
                        display='grid'
                        gap={tv(TV_BOARD_FRAME.masters.gap)}
                        gridTemplateColumns={`repeat(${visibleStations.length}, ${tv(TV_BOARD_FRAME.masters.cardWidth)})`}
                        h={tv(TV_BOARD_FRAME.masters.height)}
                        left={tv(TV_BOARD_FRAME.masters.x)}
                        minW={0}
                        position='absolute'
                        top={tv(TV_BOARD_FRAME.masters.y)}
                        w={tv(TV_BOARD_FRAME.masters.width)}
                    >
                        {visibleStations.map((station, index) => (
                            <BoardMasterCard
                                key={station.station_id ?? station.id ?? index}
                                station={station}
                            />
                        ))}
                    </Box>
                ) : (
                    <VStack alignItems='center' h='full' justifyContent='center' w='full'>
                        <Text fontSize={tv(64)} fontWeight='800'>
                            {isError ? 'Очередь недоступна' : 'Загрузка очереди'}
                        </Text>
                        {isLoading && (
                            <Text color='#71717a' fontSize={tv(36)} fontWeight='600'>
                                {isStreaming ? 'Ждем данные очереди' : 'Подключаем экран'}
                            </Text>
                        )}
                    </VStack>
                )}
            </Box>
        </Box>
    )
}
